import { useCallback, useMemo, useState } from "react";
import type {
  ProcessedViewData,
  ViewQueryParams,
} from "../services/viewService";
import { useWorkspaceContext } from "../../../providers/WorkspaceProvider";
import { useViewDataQuery } from "./useViewDataQuery";

/** The query API serves at most this many rows per page. */
export const VIEW_ROWS_PAGE_SIZE = 100;

type Row = ProcessedViewData["rows"][number];

export interface AccumulatedViewRows {
  rows: Row[];
  /** Total matching rows reported by the server, when known. */
  totalCount: number | undefined;
  /** True while the first page is loading. */
  isLoading: boolean;
  /** True while a page after the first is loading. */
  isLoadingMore: boolean;
  error: unknown;
  /** More rows exist on the server than are loaded. */
  hasMore: boolean;
  /** Request the next page; no-op while one is loading or nothing is left. */
  loadMore: () => void;
  retry: () => void;
}

/**
 * Pages through a view's rows, accumulating every page requested so far.
 *
 * The server caps a page at 100 rows, so a widget that asked for one page and rendered it
 * silently dropped every row after the first 100 (#1654). Callers show `rows` plus an explicit
 * "showing N of TOTAL" notice and call `loadMore` (or call it on their own until `hasMore` is
 * false). Changing the workspace, the view or any query parameter other than the page starts over
 * at page 1: accumulated pages are snapshots and are never carried across that scope, whatever the
 * query cache does meanwhile.
 */
export function useAccumulatedViewRows(
  viewId: string | null,
  params: Omit<ViewQueryParams, "page" | "pageSize">,
): AccumulatedViewRows {
  const { workspace } = useWorkspaceContext();
  const resetKey = JSON.stringify([workspace?.id ?? null, viewId, params]);
  // `previous` holds the pages before `page`; the current page comes from the query itself.
  const [state, setState] = useState<{
    key: string;
    page: number;
    previous: Row[];
  }>({ key: resetKey, page: 1, previous: [] });
  const current =
    state.key === resetKey ? state : { key: resetKey, page: 1, previous: [] };

  const query = useViewDataQuery(viewId, {
    ...params,
    page: current.page,
    pageSize: VIEW_ROWS_PAGE_SIZE,
  });
  const pageRows = query.data?.rows;

  const rows = useMemo(
    () => (pageRows ? [...current.previous, ...pageRows] : current.previous),
    [current.previous, pageRows],
  );
  const totalCount = query.data?.totalCount;
  const hasMore = totalCount !== undefined && rows.length < totalCount;
  const isLoading = query.isLoading === true;

  const loadMore = useCallback(() => {
    if (!hasMore || isLoading) return;
    setState({ key: resetKey, page: current.page + 1, previous: rows });
  }, [current.page, hasMore, isLoading, resetKey, rows]);

  return {
    rows,
    totalCount,
    isLoading: current.page === 1 && isLoading,
    isLoadingMore: current.page > 1 && isLoading,
    error: query.error,
    hasMore,
    loadMore,
    retry: () => {
      void query.refetch();
    },
  };
}
